import { basePath } from "@Common/constants";
import { FeatureCard } from "@Src/components/FeatureCard";
import { PublicFooter } from "@Src/components/PublicFooter";
import { PublicNav } from "@Src/components/PublicNav";
import { RetrowaveBackground } from "@Src/components/RetrowaveBackground";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import LockIcon from "@mui/icons-material/Lock";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import SecurityIcon from "@mui/icons-material/Security";
import SettingsIcon from "@mui/icons-material/Settings";
import SpeedIcon from "@mui/icons-material/Speed";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import { Box, Button, Container, Grid, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";

export const FeaturesPage = () => {
	const navigate = useNavigate();

	return (
		<Box
			sx={{
				minHeight: "100vh",
				bgcolor: "background.default",
				position: "relative",
				overflow: "hidden",
			}}
		>
			<PublicNav />

			<RetrowaveBackground />

			<Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
				{/* Hero */}
				<Box
					sx={{
						maxWidth: 800,
						mx: "auto",
						textAlign: "center",
						pt: { xs: 8, md: 12 },
						pb: { xs: 8, md: 10 },
					}}
				>
					<Typography
						variant="h1"
						sx={{
							fontSize: { xs: "2.5rem", sm: "3rem", md: "3.5rem" },
							fontWeight: 700,
							color: "text.primary",
							mb: 3,
							lineHeight: 1.2,
						}}
					>
						Powerful Features for Authentic Assessment
					</Typography>

					<Typography
						variant="h5"
						sx={{
							fontSize: { xs: "1.125rem", md: "1.25rem" },
							color: "text.secondary",
							mb: 5,
							fontWeight: 400,
							lineHeight: 1.6,
						}}
					>
						Everything you need to create, manage, and analyze assessments that
						prevent AI interference.
					</Typography>

					<Box
						sx={{
							display: "flex",
							gap: 2,
							justifyContent: "center",
							flexWrap: "wrap",
						}}
					>
						<Button
							variant="contained"
							size="large"
							onClick={() => navigate(basePath)}
							sx={{
								px: 4,
								py: 1.5,
								textTransform: "none",
								fontSize: "1rem",
								fontWeight: 600,
							}}
						>
							Get Started
						</Button>
						<Button
							variant="outlined"
							size="large"
							onClick={() => navigate("/pricing")}
							sx={{
								px: 4,
								py: 1.5,
								textTransform: "none",
								fontSize: "1rem",
								fontWeight: 600,
							}}
						>
							View Pricing
						</Button>
					</Box>
				</Box>

				{/* AI Prevention Technology */}
				<Box sx={{ pb: { xs: 8, md: 10 } }}>
					<Box
						sx={{
							maxWidth: 700,
							mx: "auto",
							textAlign: "center",
							mb: 8,
						}}
					>
						<Typography
							variant="h2"
							sx={{
								fontSize: { xs: "2rem", md: "2.5rem" },
								fontWeight: 600,
								color: "text.primary",
								mb: 2,
							}}
						>
							AI Prevention Technology
						</Typography>
						<Typography
							variant="body1"
							sx={{
								color: "text.secondary",
								fontSize: "1.125rem",
							}}
						>
							NoMachine forms system makes it difficult for respondents to use
							AI tools during assessments.
						</Typography>
					</Box>

					<Grid container spacing={4}>
						<Grid item xs={12} md={4}>
							<FeatureCard
								icon={<LockIcon fontSize="inherit" />}
								title="Secure Environment"
								description="Create a controlled assessment environment that limits access to external tools and resources."
							/>
						</Grid>
						<Grid item xs={12} md={4}>
							<FeatureCard
								icon={<SecurityIcon fontSize="inherit" />}
								title="Real-Time Detection"
								description="Monitor suspicious behavior patterns that indicate AI tool usage during assessments."
							/>
						</Grid>
						<Grid item xs={12} md={4}>
							<FeatureCard
								icon={<NotificationsActiveIcon fontSize="inherit" />}
								title="Activity Monitoring"
								description="Compare responses with one another using our analytics."
							/>
						</Grid>
					</Grid>
				</Box>

				{/* Assessment Creation and Management */}
				<Box sx={{ pb: { xs: 8, md: 10 } }}>
					<Box
						sx={{
							maxWidth: 700,
							mx: "auto",
							textAlign: "center",
							mb: 8,
						}}
					>
						<Typography
							variant="h2"
							sx={{
								fontSize: { xs: "2rem", md: "2.5rem" },
								fontWeight: 600,
								color: "text.primary",
								mb: 2,
							}}
						>
							Assessment Creation and Management
						</Typography>
						<Typography
							variant="body1"
							sx={{
								color: "text.secondary",
								fontSize: "1.125rem",
							}}
						>
							Build comprehensive assessments quickly with our intuitive tools.
						</Typography>
					</Box>

					<Grid container spacing={4}>
						<Grid item xs={12} md={4}>
							<FeatureCard
								icon={<SpeedIcon fontSize="inherit" />}
								title="Quick Setup"
								description="Create forms and assessments in minutes with our form builder"
							/>
						</Grid>
						<Grid item xs={12} md={4}>
							<FeatureCard
								icon={<SettingsIcon fontSize="inherit" />}
								title="Flexible Configuration"
								description="Customize your forms, configure branching rules to fit your needs"
							/>
						</Grid>
						<Grid item xs={12} md={4}>
							<FeatureCard
								icon={<CloudDownloadIcon fontSize="inherit" />}
								title="Bulk Export"
								description="Export easily forms responses to CSV for your processing needs"
							/>
						</Grid>
					</Grid>
				</Box>

				{/* Support and Security */}
				<Box sx={{ pb: { xs: 8, md: 10 } }}>
					<Box
						sx={{
							maxWidth: 700,
							mx: "auto",
							textAlign: "center",
							mb: 8,
						}}
					>
						<Typography
							variant="h2"
							sx={{
								fontSize: { xs: "2rem", md: "2.5rem" },
								fontWeight: 600,
								color: "text.primary",
								mb: 2,
							}}
						>
							Support and Security
						</Typography>
						<Typography
							variant="body1"
							sx={{
								color: "text.secondary",
								fontSize: "1.125rem",
							}}
						>
							We're committed to keeping your data secure and helping you
							succeed.
						</Typography>
					</Box>

					<Grid container spacing={4}>
						<Grid item xs={12} md={4}>
							<FeatureCard
								icon={<SupportAgentIcon fontSize="inherit" />}
								title="Expert Support"
								description="Get help from our dedicated support team via email when you need it."
							/>
						</Grid>
						<Grid item xs={12} md={4}>
							<FeatureCard
								icon={<LockIcon fontSize="inherit" />}
								title="Data Encryption"
								description="All data is encrypted at rest and in transit using industry-standard protocols."
							/>
						</Grid>
						<Grid item xs={12} md={4}>
							<FeatureCard
								icon={<SecurityIcon fontSize="inherit" />}
								title="Compliance Ready"
								description="Built with privacy and security standards in mind, including GDPR compliance."
							/>
						</Grid>
					</Grid>
				</Box>

				{/* CTA */}
				<Box sx={{ textAlign: "center", pb: { xs: 8, md: 12 } }}>
					<Typography
						variant="h2"
						sx={{
							fontSize: { xs: "2rem", md: "2.5rem" },
							fontWeight: 600,
							color: "text.primary",
							mb: 2,
						}}
					>
						Ready to Experience These Features?
					</Typography>
					<Typography
						variant="body1"
						sx={{
							color: "text.secondary",
							mb: 4,
							fontSize: "1.125rem",
						}}
					>
						Start creating authentic assessments today with our 14-day free
						trial.
					</Typography>
					<Box
						sx={{
							display: "flex",
							gap: 2,
							justifyContent: "center",
							flexWrap: "wrap",
						}}
					>
						<Button
							variant="contained"
							size="large"
							onClick={() => navigate(basePath)}
							sx={{
								px: 4,
								py: 1.5,
								textTransform: "none",
								fontSize: "1rem",
								fontWeight: 600,
							}}
						>
							Get Started
						</Button>
						<Button
							variant="outlined"
							size="large"
							onClick={() => navigate("/contact")}
							sx={{
								px: 4,
								py: 1.5,
								textTransform: "none",
								fontSize: "1rem",
								fontWeight: 600,
							}}
						>
							Contact Sales
						</Button>
					</Box>
				</Box>
			</Container>

			<PublicFooter />
		</Box>
	);
};
